import React, { useCallback } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowBadLanguageDetectorWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/bad-language-detector-write-back/workflow-bad-language-detector-write-back';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackFountainStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackFountainStrategy';
import { WriteBackToFountainStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';

export interface ConfigureBadLanguageDetectorWriteBackDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowBadLanguageDetectorWriteBackDetails | null;
  updateDetails: (
    details: WorkflowBadLanguageDetectorWriteBackDetails | null,
  ) => void;
}

const defaultFountainStrategy = (): WriteBackToFountainStrategy => ({
  type: 'fountain',
  authMethod: {
    type: 'merge',
  },
  placesToWrite: [{ type: 'customField', customFieldKey: '' }],
});

export const ConfigureBadLanguageDetectorWriteBackDetails = ({
  details,
  updateDetails,
  listDataSetsPattern,
  listSecretsPattern,
  organizationId,
}: ConfigureBadLanguageDetectorWriteBackDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowBadLanguageDetectorWriteBackDetails>) => {
      if (!details) {
        updateDetails(null);
        return;
      }
      updateDetails({
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'badLanguageDetectorWriteBack',
            strategy: defaultFountainStrategy(),
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode | null) => {
      updateDetailsPartial({
        mode: mode ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateStrategy = useCallback(
    (strategy: WriteBackToFountainStrategy | null) => {
      updateDetailsPartial({
        strategy: strategy ?? defaultFountainStrategy(),
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumPreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  return (
    <div className="space-y-4 p-4 bg-white rounded shadow">
      <h2 className="text-xl font-semibold">
        Configure Bad Language Detector Write Back
      </h2>
      <p className="text-gray-600">
        Writes the transform column{' '}
        <code className="text-sm bg-gray-100 px-1 rounded">
          bad_language_detector_flag
        </code>{' '}
        to a Fountain custom field (requires merge ATS data set and applicant
        Fountain IDs).
      </p>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable bad language detector write back
      </S2Checkbox>

      {details && (
        <>
          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
          />
          <ConfigureWriteBackFountainStrategy
            strategy={details.strategy ?? null}
            updateStrategy={updateStrategy}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
            allowedPlacesToWriteTypes={['customField']}
          />
          <div className="space-y-4">
            <label className="space-y-1 flex flex-col">
              <span>Number of Samples To Send</span>
              <input
                type="number"
                value={details.numberOfSamplesToSend ?? ''}
                onChange={(e) => {
                  updateNumberOfSamplesToSend(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
            </label>

            <label className="space-y-1 flex flex-col">
              <span>Number of Previous Days of Applicants</span>
              <input
                type="number"
                value={details.numPreviousDaysOfApplicants ?? ''}
                onChange={(e) => {
                  updateNumPreviousDaysOfApplicants(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
              <p className="text-sm text-gray-500">
                Applicants without an application date in range are skipped.
              </p>
            </label>
          </div>
        </>
      )}
    </div>
  );
};
